import React, { useState, useEffect, useCallback } from 'react';
import { FileText } from 'lucide-react';
import EmptyState from '../common/EmptyState';
import DocumentReview from './DocumentReview';
import { AdminCharterAPI } from '../../services/api';
import { DOC_TYPE_LABELS, ownerLabel, formatDay, formatDateTime, apiErrorMessage } from './charterUtils';

const TABS = [
  { id: 'pending', label: 'Pending' },
  { id: 'verified', label: 'Verified' },
];

/**
 * Documents (spec 6.4): the charter document queue. Pending oldest first,
 * Verified newest first. Selecting a row opens the review panel.
 */
const DocumentQueue = () => {
  const [tab, setTab] = useState('pending');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await AdminCharterAPI.listDocuments(tab);
      setItems(res.data.data.items || []);
      setError('');
    } catch (e) {
      setItems([]);
      setError(apiErrorMessage(e, 'Could not load the documents'));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const selected = items.find((d) => d._id === selectedId) || null;

  const onReviewed = async () => {
    setSelectedId(null);
    await load();
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Documents</h1>
      <p className="text-sm text-gray-500 mb-6">Charter licences, PrDPs and passenger liability insurance, checked against the file.</p>

      <div className="border-b border-gray-200 mb-4 flex space-x-6">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => { setTab(t.id); setSelectedId(null); }}
            className={`pb-3 px-1 border-b-2 text-sm font-medium ${tab === t.id ? 'border-emerald-600 text-emerald-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <p className="mb-4 text-sm text-red-600" role="alert">{error}</p>}

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <div className="xl:col-span-3 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading && <div className="p-6 text-sm text-gray-500">Loading...</div>}
          {!loading && items.length === 0 && (
            <EmptyState title={tab === 'pending' ? 'Nothing to review' : 'No verified documents'} message="Documents operators upload in Charter Rates appear here." icon={FileText} />
          )}
          {!loading && items.length > 0 && (
            <ul className="divide-y divide-gray-100">
              {items.map((doc) => (
                <li key={doc._id}>
                  <button
                    onClick={() => setSelectedId(doc._id)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 ${selectedId === doc._id ? 'bg-emerald-50' : ''}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-medium text-gray-900">{DOC_TYPE_LABELS[doc.docType] || doc.docType}</span>
                      <span className="text-xs text-gray-500">
                        {tab === 'pending' ? `Uploaded ${formatDateTime(doc.createdAt)}` : `Verified ${formatDateTime(doc.verifiedAt || doc.reviewedAt)}`}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      {(doc.companyInfo && doc.companyInfo.companyName) || 'Unknown operator'} · {ownerLabel(doc)}
                    </div>
                    {doc.expiresAt && <div className="text-xs text-gray-500">Expires {formatDay(doc.expiresAt)}</div>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="xl:col-span-2">
          {selected
            ? <DocumentReview key={selected._id} doc={selected} onDone={onReviewed} onClose={() => setSelectedId(null)} />
            : <div className="text-sm text-gray-500 bg-white rounded-lg border border-dashed border-gray-300 p-6">Select a document to review it.</div>}
        </div>
      </div>
    </div>
  );
};

export default DocumentQueue;
